import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { Toaster } from './components/ui/sonner';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import ComplianceDashboard from './pages/ComplianceDashboard';
import DriverManagement from './pages/DriverManagement';
import Units from './pages/Units';
import Drivers from './pages/Drivers';
import Staff from './pages/Staff';
import OwnerOperators from './pages/OwnerOperators';
import Loads from './pages/Loads';
import Deductions from './pages/Deductions';
import RecurringDeductions from './pages/RecurringDeductions';
import Summary from './pages/Summary';
import History from './pages/History';
import VehicleManagement from './pages/VehicleManagement';
import DriverFiles from './pages/DriverFiles';
import UserManagement from './pages/UserManagement';
import CompanySettings from './pages/CompanySettings';
import SuperAdmin from './pages/SuperAdmin';
import DriverApplication from './pages/DriverApplication';
import Schedule from './pages/Schedule';
import Accounting from './pages/Accounting';
import Dispatchers from './pages/Dispatchers';
import PaystubsHub from './pages/PaystubsHub';
import './App.css';

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return <Layout>{children}</Layout>;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Root route - Dashboard for logged in users, Landing for guests */}
      <Route path="/" element={user ? <Layout><Dashboard /></Layout> : <LandingPage />} />
      
      {/* Public Landing Page - always accessible */}
      <Route path="/home" element={<LandingPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" replace /> : <Register />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/driver-management" element={<ProtectedRoute><DriverManagement /></ProtectedRoute>} />
      <Route path="/units" element={<ProtectedRoute><Units /></ProtectedRoute>} />
      <Route path="/drivers" element={<ProtectedRoute><Drivers /></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><Staff /></ProtectedRoute>} />
      <Route path="/owner-operators" element={<ProtectedRoute><OwnerOperators /></ProtectedRoute>} />
      <Route path="/dispatchers" element={<ProtectedRoute><Dispatchers /></ProtectedRoute>} />
      <Route path="/paystubs" element={<ProtectedRoute><PaystubsHub /></ProtectedRoute>} />
      <Route path="/loads" element={<ProtectedRoute><Loads /></ProtectedRoute>} />
      <Route path="/deductions" element={<ProtectedRoute><Deductions /></ProtectedRoute>} />
      <Route path="/recurring-deductions" element={<ProtectedRoute><RecurringDeductions /></ProtectedRoute>} />
      <Route path="/summary" element={<ProtectedRoute><Summary /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
      <Route path="/vehicle-management" element={<ProtectedRoute><VehicleManagement /></ProtectedRoute>} />
      <Route path="/driver-files" element={<ProtectedRoute><DriverFiles /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
      <Route path="/accounting" element={<ProtectedRoute><Accounting /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/users" element={<ProtectedRoute adminOnly><UserManagement /></ProtectedRoute>} />
      <Route path="/company" element={<ProtectedRoute adminOnly><CompanySettings /></ProtectedRoute>} />

      {/* Super Admin Route (protected by password on the page itself) */}
      <Route path="/super-admin" element={<SuperAdmin />} />

      {/* Public Driver Application (no auth required) */}
      <Route path="/apply" element={<DriverApplication />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
